import React, { useState, useCallback } from "react";
import useAncestryTree, { formatPersonName } from "./useAncestryTree";
import LineageColumn from "./LineageColumn";
import PersonCard from "./PersonCard";

/**
 * LineageView — full-screen ancestry mini-tree.
 *
 * Props:
 *   tree        – the flat family tree array
 *   targetId    – the person whose ancestry to show
 *   onClose     – callback to return to the full tree
 *   onNavigate  – callback when clicking a person in the lineage (for click-to-navigate)
 */
function LineageView({ tree, targetId, onClose, onNavigate, breadcrumbs }) {
  const { ancestryRoot, pathMap, maxGeneration, lineage } = useAncestryTree(tree, targetId);
  const [highlightedPath, setHighlightedPath] = useState(new Set());

  // When the user hovers over a card, highlight the path from target to that person
  const handleMouseEnter = useCallback(
    (personId) => {
      const path = pathMap.get(String(personId));
      if (path) {
        setHighlightedPath(new Set(path));
      }
    },
    [pathMap]
  );

  const handleMouseLeave = useCallback(() => {
    setHighlightedPath(new Set());
  }, []);

  // Click a name to navigate to THAT person's ancestry
  const handleCardClick = useCallback(
    (personId) => {
      if (onNavigate && String(personId) !== String(targetId)) {
        onNavigate(personId);
      }
    },
    [onNavigate, targetId]
  );

  if (!ancestryRoot) {
    return (
      <div className="lineage-view">
        <div className="lineage-header">
          <button type="button" className="lineage-back-btn" onClick={onClose}>
            Back to Full Tree
          </button>
          <div className="lineage-header-title">
            {/* Named the same way as the normal header below. */}
            <span className="lineage-mode-label">Lineage Mode</span>
            No ancestry data found
          </div>
        </div>
      </div>
    );
  }

  // Build the target person's display name for the header
  const targetName = formatPersonName(ancestryRoot.data);

  return (
    <div className="lineage-view">
      {/* Header */}
      <div className="lineage-header">
        <button type="button" className="lineage-back-btn" onClick={onClose}>
          Back to Full Tree
        </button>
        {/* Breadcrumbs */}
        {breadcrumbs && breadcrumbs.length > 1 && (
          <div className="lineage-breadcrumbs">
            {breadcrumbs.map((crumb, i) => (
              <span key={crumb.id}>
                {i > 0 && <span className="lineage-breadcrumb-sep">{" > "}</span>}
                {i < breadcrumbs.length - 1 ? (
                  <button
                    type="button"
                    className="lineage-breadcrumb-link"
                    onClick={() => onNavigate(crumb.id, i)}
                  >
                    {crumb.name}
                  </button>
                ) : (
                  <span className="lineage-breadcrumb-current">{crumb.name}</span>
                )}
              </span>
            ))}
          </div>
        )}
        <div className="lineage-header-title">
          {/* Which mode this is, a little apart from the name (Sam, 2026-09-27). */}
          <span className="lineage-mode-label">Lineage Mode</span>
          Ancestry of {targetName}
        </div>
      </div>

      {/* Narrow single-column lineage — oldest ancestors at top, target at bottom */}
      <div className="lineage-generations">
        {/* One block, centred on itself: the target card stays under its
            parents' connector even when the lineage is wider than the screen
            (and .lineage-generations then starts the block at the left edge). */}
        <div className="flex flex-col items-center">
          <LineageColumn
            gen={lineage}
            highlightedPath={highlightedPath}
            onHover={handleMouseEnter}
            onLeave={handleMouseLeave}
            onClick={handleCardClick}
          />

          {/* Target person's card at the bottom */}
          <PersonCard
            node={ancestryRoot}
            isTarget={true}
            isHighlighted={highlightedPath.has(String(ancestryRoot.id))}
            onMouseEnter={() => handleMouseEnter(ancestryRoot.id)}
            onMouseLeave={handleMouseLeave}
            onClick={null}
          />

          {/* No parents message */}
          {maxGeneration === 0 && (
            <div className="lineage-no-ancestors">
              No further ancestors recorded
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LineageView;
